// Times one in-app SPA route change: router.beforeEach (start) → Nuxt `page:finish`
// (new page rendered and its async setup resolved). The initial hard load and
// same-route replaces (query/hash-only changes) are not measured. Only route NAMES are
// reported — never path params, query or ids.

/** The subset of a vue-router RouteLocation the timer reads. */
export interface NavigationRoute {
    name?: string | symbol | null
    path: string
    matched: readonly unknown[]
}

export interface PageNavigation {
    route: string
    fromRoute: string
    durationMs: number
}

export interface PageNavigationTimer {
    /** router.beforeEach: begin timing unless this is the initial load or a same-route replace. */
    start(to: NavigationRoute, from: NavigationRoute): void
    /** Navigation aborted / failed / errored. */
    cancel(): void
    /** Nuxt `page:finish`: emit once for the pending navigation, if any. */
    finish(): void
}

/** Same value `faro.api.setView({ name })` uses in plugins/9.faro.client.ts. */
const viewName = (route: NavigationRoute) => (route.name ?? route.path).toString()

export const createPageNavigationTimer = (emit: (navigation: PageNavigation) => void): PageNavigationTimer => {
    let pending: { route: string; fromRoute: string; startedAt: number } | null = null

    return {
        start(to, from) {
            pending = null
            // START_LOCATION (no matched records) = the initial hard load, covered by web-vitals.
            if (from.matched.length === 0) return
            if (to.path === from.path && to.name === from.name) return
            pending = { route: viewName(to), fromRoute: viewName(from), startedAt: performance.now() }
        },

        cancel() {
            pending = null
        },

        finish() {
            const navigation = pending
            if (!navigation) return
            pending = null
            try {
                emit({
                    route: navigation.route,
                    fromRoute: navigation.fromRoute,
                    durationMs: Math.round(performance.now() - navigation.startedAt),
                })
            } catch {
                // Observability must never break navigation.
            }
        },
    }
}
